import {
  type DateValue,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import { Calendar, Type02 } from "@untitledui/icons";
import type React from "react";
import { useMemo, useState } from "react";
import { useDateFormatter } from "react-aria";
import { Button as AriaButton, useLocale } from "react-aria-components";
import {
  DateRangeCalendarPopover,
  type DateRangePreset,
} from "@/components/application/date-picker/date-range-calendar-popover";
import { Badge } from "@/components/base/badges/badges";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { REPORT_TEMPLATES } from "@/constants/reportTemplates";
import { cx } from "@/utils/cx";
import { generateDateRangePresets } from "@/utils/datePresets";
import { TemplateBarChartIcon01 } from "../graphics/TemplateBarChartIcon01";
import { TemplateBarChartIcon02 } from "../graphics/TemplateBarChartIcon02";
import { TemplateLineChartIcon } from "../graphics/TemplateLineChartIcon";
import { TemplateTableIcon } from "../graphics/TemplateTableIcon";
import type { ReportTemplate } from "../types";

export interface TemplateCardProps {
  template: ReportTemplate;
  isSelected: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

// Helper function to get the appropriate preview icon based on template
const getPreviewIcon = (template: ReportTemplate, isSelected: boolean) => {
  // Map template IDs to specific icons
  switch (template.id) {
    case "product-gmv-revenue-source":
      return <TemplateBarChartIcon02 isSelected={isSelected} />; // Product GMV two-tier bars
    case "creator-community-health":
      return <TemplateBarChartIcon01 isSelected={isSelected} />; // Creator health stacked bars
    case "content-analysis":
      return <TemplateLineChartIcon isSelected={isSelected} />; // Content analysis line chart
    case "content-efficiency":
      return <TemplateLineChartIcon isSelected={isSelected} />; // Content efficiency line chart
    case "pattern-analysis":
      return <TemplateTableIcon isSelected={isSelected} />; // Pattern analysis table
    default:
      // Fallback based on layout
      switch (template.layout) {
        case "table":
          return <TemplateTableIcon isSelected={isSelected} />;
        case "bar":
          return <TemplateBarChartIcon01 isSelected={isSelected} />;
        case "chart":
          return <TemplateLineChartIcon isSelected={isSelected} />;
      }
  }
};

interface TemplateFormConfigurationProps {
  selectedTemplate: ReportTemplate | null;
  onTemplateSelect: (template: ReportTemplate) => void;
  reportName: string;
  onReportNameChange: (name: string) => void;
  timePeriod: "lastMonth" | "lastWeek" | "specificRange";
  onTimePeriodChange: (period: "lastMonth" | "lastWeek" | "specificRange") => void;
  dateRange: { start: DateValue; end: DateValue } | null;
  onDateRangeChange: (range: { start: DateValue; end: DateValue } | null) => void;
  limits?: { minDate: DateValue; maxDate: DateValue };
}

// Inline TemplateCard component
const TemplateCard: React.FC<{
  template: ReportTemplate;
  isSelected: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}> = ({ template, isSelected, onClick, children }) => {
  if (isSelected && children) {
    // Selected card with time period - one container with flex-col layout
    return (
      <div
        role="radio"
        aria-checked={isSelected}
        aria-label={`Select ${template.name} template`}
        onClick={onClick}
        className={cx(
          "self-stretch min-w-20 p-5 bg-secondary rounded-xl flex flex-col justify-start items-start gap-6 cursor-pointer",
          "outline outline-2 outline-offset-[-2px] outline-brand",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
        )}
      >
        {/* Template Info Section */}
        <div className="self-stretch inline-flex justify-start items-start gap-4">
          {/* Preview Icon */}
          <div className="w-[121px] self-stretch flex items-center justify-center">
            {getPreviewIcon(template, isSelected)}
          </div>

          {/* Template Info */}
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
            <div className="self-stretch justify-start text-primary text-sm font-semibold leading-5">
              {template.name}
            </div>
            <div className="self-stretch justify-start text-secondary text-sm font-medium leading-5">
              {template.description}
            </div>
            <Badge color="gray" size="sm">
              {template.category}
            </Badge>
          </div>

          {/* Radio Button */}
          <div className="w-4 h-4 relative bg-brand-solid rounded-full overflow-hidden" aria-hidden="true">
            <div className="w-1.5 h-1.5 left-[5px] top-[5px] absolute bg-white rounded-full" />
          </div>
        </div>

        {/* Time Period Section */}
        {children}
      </div>
    );
  }

  // Unselected card - simple button
  return (
    <button
      type="button"
      onClick={onClick}
      role="radio"
      aria-checked={isSelected}
      aria-label={`Select ${template.name} template`}
      className={cx(
        "self-stretch min-w-20 p-5 bg-primary rounded-xl inline-flex justify-start items-start gap-4 w-full text-left transition-all",
        "outline outline-1 outline-offset-[-1px] outline-secondary hover:outline-primary",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
      )}
    >
      {/* Preview Icon */}
      <div className="w-[121px] self-stretch flex items-center justify-center">
        {getPreviewIcon(template, isSelected)}
      </div>

      {/* Template Info */}
      <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
        <div className="self-stretch justify-start text-primary text-sm font-semibold leading-5">{template.name}</div>
        <div className="self-stretch justify-start text-secondary text-sm font-medium leading-5">
          {template.description}
        </div>
        <Badge color="gray" size="sm">
          {template.category}
        </Badge>
      </div>

      {/* Radio Button */}
      <div className="w-4 h-4 relative rounded-full border border-primary" aria-hidden="true" />
    </button>
  );
};

export const TemplateFormConfiguration: React.FC<TemplateFormConfigurationProps> = ({
  selectedTemplate,
  onTemplateSelect,
  reportName,
  onReportNameChange,
  timePeriod,
  onTimePeriodChange,
  dateRange,
  onDateRangeChange,
  limits,
}) => {
  // Local state for the DateRangePicker (used when "Specific range" is selected)
  const [tempDateRange, setTempDateRange] = useState<{ start: DateValue; end: DateValue } | null>(dateRange);
  const [selectedPresetKey, setSelectedPresetKey] = useState<string | null>(null);

  // Get locale and formatter for preset generation
  const { locale } = useLocale();
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Generate date range presets
  const presets: Record<string, DateRangePreset> = useMemo(() => {
    return generateDateRangePresets(locale, limits, formatter);
  }, [locale, limits, formatter]);

  // Handle preset selection from sidebar
  const handlePresetSelect = (key: string, preset: DateRangePreset) => {
    setTempDateRange(preset.value);
    setSelectedPresetKey(key);
  };

  // Calculate date range based on time period selection
  const calculateDateRange = (type: "lastMonth" | "lastWeek"): { start: DateValue; end: DateValue } => {
    const now = today(getLocalTimeZone());
    if (type === "lastMonth") {
      const lastMonthStart = startOfMonth(now).subtract({ months: 1 });
      const lastMonthEnd = endOfMonth(now).subtract({ months: 1 });
      return { start: lastMonthStart, end: lastMonthEnd };
    }
    // lastWeek
    const lastWeekStart = startOfWeek(now, "en-US").subtract({ weeks: 1 });
    const lastWeekEnd = endOfWeek(now, "en-US").subtract({ weeks: 1 });
    return { start: lastWeekStart, end: lastWeekEnd };
  };

  // Check if a preset is available (within date limits)
  const isPresetAvailable = (type: "lastMonth" | "lastWeek"): boolean => {
    if (!limits) return true;
    const range = calculateDateRange(type);
    return range.start.compare(limits.minDate) >= 0 && range.end.compare(limits.maxDate) <= 0;
  };

  // Handle preset button clicks
  const handlePresetClick = (preset: "lastMonth" | "lastWeek") => {
    if (!isPresetAvailable(preset)) return; // Don't allow if outside date limits
    onTimePeriodChange(preset);
    const range = calculateDateRange(preset);
    onDateRangeChange(range);
    setTempDateRange(range);
  };

  return (
    <>
      {/* Template List with embedded DateRangePicker */}
      <div className="flex flex-col gap-2 w-full">
        {REPORT_TEMPLATES.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate?.id === template.id}
            onClick={() => onTemplateSelect(template)}
          >
            {/* Time Period Selector - shown only when this template is selected */}
            {selectedTemplate?.id === template.id && (
              <div className="self-stretch flex flex-col justify-start items-start gap-2 overflow-hidden mt-3 pt-6 border-t border-secondary">
                <div className="self-stretch inline-flex justify-center items-center gap-1.5">
                  <Calendar className="size-5 text-tertiary" />
                  <div className="flex-1 justify-start text-secondary text-sm font-medium leading-5">Time Period</div>
                </div>
                <div className="self-stretch text-xs text-quaternary">The time frame for your report</div>

                <div className="self-stretch p-1 bg-primary rounded-lg outline outline-1 outline-offset-[-1px] outline-primary inline-flex justify-start items-start gap-1">
                  <button
                    type="button"
                    onClick={() => handlePresetClick("lastMonth")}
                    disabled={!isPresetAvailable("lastMonth")}
                    className={
                      timePeriod === "lastMonth"
                        ? "flex-1 h-8 px-2 py-2 bg-secondary rounded-lg outline outline-1 outline-offset-[-1px] outline-fg-primary flex justify-center items-center overflow-hidden whitespace-nowrap"
                        : "flex-1 h-8 px-2 py-2 flex justify-center items-center overflow-hidden whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    }
                  >
                    <div className="text-primary text-sm font-medium leading-5">Last Month</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePresetClick("lastWeek")}
                    disabled={!isPresetAvailable("lastWeek")}
                    className={
                      timePeriod === "lastWeek"
                        ? "flex-1 h-8 px-2 py-2 bg-secondary rounded-lg outline outline-1 outline-offset-[-1px] outline-fg-primary flex justify-center items-center overflow-hidden whitespace-nowrap"
                        : "flex-1 h-8 px-2 py-2 flex justify-center items-center overflow-hidden whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    }
                  >
                    <div className="text-primary text-sm font-medium leading-5">Last Week</div>
                  </button>
                  <div className="flex-1">
                    <DateRangeCalendarPopover
                      value={tempDateRange}
                      onChange={(value) => {
                        setTempDateRange(value);
                        setSelectedPresetKey(null); // Clear preset when manually selecting dates
                      }}
                      onApply={() => {
                        if (tempDateRange) {
                          onDateRangeChange(tempDateRange);
                          onTimePeriodChange("specificRange");
                        }
                      }}
                      onReset={() => {
                        const now = today(getLocalTimeZone());
                        const thirtyOneDaysAgo = now.subtract({ days: 31 });
                        const resetRange = { start: thirtyOneDaysAgo, end: now };
                        setTempDateRange(resetRange);
                        onDateRangeChange(resetRange);
                        onTimePeriodChange("specificRange");
                        setSelectedPresetKey(null);
                      }}
                      resetText="Last 31 days"
                      minValue={limits?.minDate}
                      maxValue={limits?.maxDate}
                      placement="bottom right"
                      showPresets={true}
                      presets={presets}
                      selectedPresetKey={selectedPresetKey}
                      onPresetSelect={handlePresetSelect}
                    >
                      <AriaButton
                        className={cx(
                          "flex-1 h-8 px-2 py-2 rounded-lg flex justify-center items-center overflow-hidden whitespace-nowrap",
                          timePeriod === "specificRange"
                            ? "bg-secondary outline outline-1 outline-offset-[-1px] outline-fg-primary"
                            : "",
                        )}
                      >
                        <div className="text-primary text-sm font-medium leading-5">
                          {timePeriod === "specificRange" && dateRange
                            ? `${dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                            : "Specific range"}
                        </div>
                      </AriaButton>
                    </DateRangeCalendarPopover>
                  </div>
                </div>
              </div>
            )}
          </TemplateCard>
        ))}
      </div>

      {/* Report Details - always shown */}
      <div className="flex gap-17.5 pt-6 border-t border-secondary w-full">
        <div className="flex flex-col gap-1 w-[20%] shrink-0">
          <div className="flex gap-1.5 items-center">
            <Type02 className="size-5 text-tertiary" />
            <span className="text-sm font-medium text-secondary">Report Details</span>
          </div>
          <span className="text-xs text-quaternary">Name</span>
        </div>

        <div className="flex flex-col gap-2 flex-1 w-[80%]">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-report-name">Name</Label>
            <Input
              id="template-report-name"
              type="text"
              value={reportName}
              onChange={onReportNameChange}
              maxLength={40}
            />
            <span className="text-sm text-tertiary">Maximum 40 Characters</span>
          </div>
        </div>
      </div>
    </>
  );
};
